/**
 * First/last visible-column alignment for Dispatch menus and stage-log popovers.
 * Keyed off column index in the currently visible strip (5 or 7 days), not
 * calendar weekday and not a hardcoded Friday index.
 */

import { getDateString } from "../../../utils/datetime";

/**
 * Visible-column edge for Dispatch overlays.
 *
 * @param {number} dayIndex Column index in the visible week strip.
 * @param {number} visibleCount Number of visible columns (5 or 7).
 * @returns {{
 *   isFirst: boolean,
 *   isLast: boolean,
 *   edge: "start"|"end"|"middle",
 *   menuAlign: "left"|"right",
 * }}
 */
export function getDispatchOverlayAlign(dayIndex, visibleCount) {
  const isFirst = dayIndex === 0;
  const isLast = visibleCount > 0 && dayIndex === visibleCount - 1;
  let edge = "middle";
  if (isFirst) {
    edge = "start";
  } else if (isLast) {
    edge = "end";
  }
  return {
    isFirst,
    isLast,
    edge,
    menuAlign: isLast ? "right" : "left",
  };
}

/**
 * Absolute position classes for the dispatch-stage history popover.
 *
 * @param {"start"|"end"|"middle"} edge Visible-column edge.
 * @returns {string}
 */
export function getDispatchStageLogPositionClass(edge) {
  if (edge === "start") {
    return "left-0 origin-top-left";
  }
  if (edge === "end") {
    return "right-0 origin-top-right";
  }
  return "left-1/2 -translate-x-1/3 origin-top";
}

/**
 * Overlay align for a calendar date in the visible Dispatch strip.
 *
 * @param {Date|string} date Calendar date or YYYY-MM-DD string.
 * @param {Array<Date>} datesOfWeek Visible week dates.
 * @returns {{
 *   isFirst: boolean,
 *   isLast: boolean,
 *   edge: "start"|"end"|"middle",
 *   menuAlign: "left"|"right",
 * }}
 */
export function getDispatchOverlayAlignForDate(date, datesOfWeek) {
  const dateKey = typeof date === "string" ? date : getDateString(date);
  const dayIndex = datesOfWeek.findIndex(
    (day) => getDateString(day) === dateKey
  );
  return getDispatchOverlayAlign(
    dayIndex >= 0 ? dayIndex : 0,
    datesOfWeek.length
  );
}
